import { useEffect, useMemo, useState } from "react"
import fp from "lodash/fp"
import { useSelector } from "react-redux"

import type { Powerline } from "types/powerlines.types"

import { selectCurrentOrgId } from "features/store/orgSlice"
import { useLazyPowerlinesQuery } from "features/api"

interface UseAllPowerlinesOptions {
  skip?: boolean
}

export const useAllPowerlines = ({ skip = false }: UseAllPowerlinesOptions = {}) => {
  const orgId = useSelector(selectCurrentOrgId)
  const [allPowerlines, setAllPowerlines] = useState<Powerline[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<any>(null)
  const [getPowerlines] = useLazyPowerlinesQuery()

  useEffect(() => {
    if (!orgId || skip) return
    let isMounted = true
    const fetchAll = async () => {
      setLoading(true)
      setError(null)
      try {
        let cursor: string | undefined = undefined
        let results: Powerline[] = []
        do {
          const { data, error } = await getPowerlines(
            {
              params: { org_id: Number(orgId), limit: 100, cursor },
            },
            true,
          )
          if (error) throw error
          if (data?.results) {
            results = results.concat(data.results)
          }
          cursor = data?.next
        } while (cursor)
        if (isMounted) {
          setAllPowerlines(results)
        }
      } catch (err) {
        if (isMounted) setError(err)
      } finally {
        if (isMounted) setLoading(false)
      }
    }
    fetchAll()
    return () => {
      isMounted = false
    }
  }, [orgId, getPowerlines, skip])

  const powerlinesById = useMemo(() => {
    return fp.keyBy((p: Powerline) => p.id, allPowerlines)
  }, [allPowerlines])

  const powerlineIdByTowerId = useMemo(() => {
    const towerMap: Record<number, number> = {}
    allPowerlines.forEach((powerline) => {
      powerline.tower_ids?.forEach((towerId) => {
        if (!towerMap[towerId]) {
          towerMap[towerId] = powerline.id
        }
      })
    })
    return towerMap
  }, [allPowerlines])

  // Every line that lists the tower. Two circuits can share the same towers.
  const powerlineIdsByTowerId = useMemo(() => {
    const towerMap: Record<number, number[]> = {}
    allPowerlines.forEach((powerline) => {
      powerline.tower_ids?.forEach((towerId) => {
        towerMap[towerId] = [...(towerMap[towerId] ?? []), powerline.id]
      })
    })
    return towerMap
  }, [allPowerlines])

  return {
    powerlines: allPowerlines,
    powerlinesById,
    powerlineIdByTowerId,
    powerlineIdsByTowerId,
    isLoading: loading,
    isError: error,
  }
}
